import React, { useState } from 'react';
import { LoginScreen } from '../screens/LoginScreen';
import { JoinScreen } from '../screens/JoinScreen';
import { ProfileSetupScreen } from '../screens/ProfileSetupScreen';
import { PermissionsScreen } from '../screens/PermissionsScreen';
import { WelcomeScreen } from '../screens/WelcomeScreen';
import { signOut } from '../../../services/authService';
import './AuthFlow.css';

export type AuthStep = 'login' | 'join' | 'profile' | 'permissions' | 'welcome' | 'complete';

export interface AuthProfileData {
  firstName: string;
  ageRange: string;
  region: string;
  bio?: string;
  avatarBlob?: Blob;
}

export interface AuthFlowProps {
  initialStep?: AuthStep;
  onComplete: (profileData?: AuthProfileData) => void;
  projectId?: string;
  projectName?: string;
  organiserName?: string;
  participantEmail?: string;
  participantName?: string;
  taskCount?: number;
  durationDays?: number;
  welcomeTitle?: string;
  welcomeMessage?: string;
}

export const AuthFlow: React.FC<AuthFlowProps> = ({
  initialStep = 'login',
  onComplete,
  projectId,
  projectName,
  organiserName,
  participantEmail,
  participantName,
  taskCount,
  durationDays,
  welcomeTitle,
  welcomeMessage,
}) => {
  const [step, setStep] = useState<AuthStep>(initialStep);
  const [profileData, setProfileData] = useState<AuthProfileData | undefined>();

  const goTo = (nextStep: AuthStep) => {
    if (nextStep === 'complete') {
      onComplete(profileData);
    } else {
      setStep(nextStep);
    }
  };

  return (
    <div className="p-auth-flow">
      {step === 'login' && (
        <div className="p-auth-screen" key="login">
          <LoginScreen onContinue={() => goTo(projectId ? 'join' : 'profile')} />
        </div>
      )}
      {step === 'join' && (
        <div className="p-auth-screen" key="join">
          <JoinScreen
            projectId={projectId}
            projectName={projectName}
            organiserName={organiserName}
            taskCount={taskCount}
            onContinue={() => goTo('profile')}
            onBack={() => goTo('login')}
            onDecline={async () => {
              await signOut();
              setStep('login');
            }}
          />
        </div>
      )}
      {step === 'profile' && (
        <div className="p-auth-screen" key="profile">
          <ProfileSetupScreen
            email={participantEmail}
            projectName={projectName}
            onContinue={(profile) => {
              setProfileData(profile);
              goTo('permissions');
            }}
          />
        </div>
      )}
      {step === 'permissions' && (
        <div className="p-auth-screen" key="permissions">
          <PermissionsScreen
            onContinue={() => goTo('welcome')}
            onSkip={() => goTo('welcome')}
          />
        </div>
      )}
      {step === 'welcome' && (
        <div className="p-auth-screen" key="welcome">
          <WelcomeScreen
            participantName={profileData?.firstName || participantName}
            organiserName={organiserName}
            taskCount={taskCount}
            durationDays={durationDays}
            welcomeTitle={welcomeTitle}
            welcomeMessage={welcomeMessage}
            onContinue={() => goTo('complete')}
          />
        </div>
      )}
    </div>
  );
};
